<template>
  <div class="admin-page">
    <div class="admin-container">
      <div class="admin-header">
        <div class="admin-header__titles">
          <h1 class="admin-title"><i class="fa-solid fa-tags"></i> Metadata Management</h1>
          <p class="admin-subtitle">Manage dynamic page metadata across the platform.</p>
        </div>
        <div class="admin-header__actions">
          <button class="admin-btn admin-btn--ghost" @click="$router.push('/')">
            <i class="fa-solid fa-house"></i> Go to Site
          </button>
        </div>
      </div>

      <!-- Initialize Button -->
      <div class="admin-card admin-section init-section">
        <button @click="toggleInitSection" class="section-header">
          <i class="fa-solid fa-chevron-right chevron" :class="{ expanded: showInitSection }"></i>
          Initialize Default Metadata
        </button>
        <div v-show="showInitSection" class="section-content">
          <button @click="initializeMetadata" class="admin-btn admin-btn--primary">
            <i class="fa-solid fa-rotate"></i> Initialize Now
          </button>
          <p class="admin-muted info">
            This will populate the database with default metadata for all pages.
          </p>
        </div>
      </div>

      <!-- Add/Edit Form -->
      <div class="admin-card admin-section">
        <div class="admin-card__head">
          <h2 class="admin-card__title">{{ isEditing ? "Edit" : "Add New" }} Metadata</h2>
        </div>
        <form @submit.prevent="saveMetadata">
          <div class="admin-field form-group">
            <label class="admin-label">Page Name (e.g., /rentals)</label>
            <input
              class="admin-input"
              v-model="formData.pageName"
              type="text"
              placeholder="/rentals"
              required
            />
          </div>

          <div class="admin-field form-group">
            <label class="admin-label">Meta Tags (JSON Format)</label>
            <textarea
              class="admin-textarea mono"
              v-model="formData.metaTagsJson"
              placeholder='{"title": "Page Title", "description": "Page description"}'
              rows="10"
            ></textarea>
            <small class="admin-muted">Enter valid JSON with key-value pairs</small>
          </div>

          <div class="admin-field form-group">
            <label class="admin-label">Description (optional)</label>
            <input
              class="admin-input"
              v-model="formData.description"
              type="text"
              placeholder="Description of this metadata"
            />
          </div>

          <div class="button-group">
            <button type="submit" class="admin-btn admin-btn--primary">
              {{ isEditing ? "Update" : "Add" }} Metadata
            </button>
            <button
              v-if="isEditing"
              type="button"
              @click="resetForm"
              class="admin-btn admin-btn--outline"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <!-- Metadata List -->
      <div class="admin-section">
        <h2 class="admin-card__title list-title">Existing Metadata</h2>
        <div v-if="metadataList.length === 0" class="admin-empty">
          <i class="fa-solid fa-tags"></i>
          <p class="admin-empty__title">No metadata found</p>
          <p>Create one or initialize defaults.</p>
        </div>

        <div v-for="item in metadataList" :key="item._id" class="admin-card admin-card--flush metadata-card">
          <div class="card-header">
            <h3 class="card-title">{{ item.pageName }}</h3>
            <small class="admin-muted">{{ item.description }}</small>
          </div>

          <div class="card-content">
            <pre>{{ JSON.stringify(item.metaTags, null, 2) }}</pre>
          </div>

          <div class="card-actions">
            <button @click="editMetadata(item)" class="admin-btn admin-btn--outline admin-btn--sm">
              <i class="fa-solid fa-pen"></i> Edit
            </button>
            <button @click="deleteMetadata(item.pageName)" class="admin-btn admin-btn--danger admin-btn--sm">
              <i class="fa-solid fa-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import axiosInst from "@/utils/axiosinstance";
import { toast } from "vue-sonner";

const router = useRouter();

const metadataList = ref([]);
const isEditing = ref(false);
const showInitSection = ref(false);

const formData = ref({
  pageName: "",
  metaTagsJson: "",
  description: "",
});

// Initialize metadata with defaults
const initializeMetadata = async () => {
  try {
    const response = await axiosInst.post("/api/metadata/initialize");
    toast.success("Metadata initialized successfully!");
    fetchAllMetadata();
  } catch (error) {
    toast.error("Failed to initialize metadata: " + error.message);
  }
};

// Toggle init section
const toggleInitSection = () => {
  showInitSection.value = !showInitSection.value;
};

// Navigate back to dashboard
const goToDashboard = () => {
  router.push("/");
};

// Fetch all metadata
const fetchAllMetadata = async () => {
  try {
    const response = await axiosInst.get("/api/metadata");
    metadataList.value = response.data.data || [];
  } catch (error) {
    toast.error("Failed to fetch metadata: " + error.message);
  }
};

// Save metadata
const saveMetadata = async () => {
  try {
    // Validate JSON
    let metaTags;
    try {
      metaTags = JSON.parse(formData.value.metaTagsJson);
    } catch (e) {
      toast.error("Invalid JSON in meta tags: " + e.message);
      return;
    }

    const payload = {
      pageName: formData.value.pageName.toLowerCase(),
      metaTags,
      description: formData.value.description,
    };

    await axiosInst.post("/api/metadata/upsert", payload);
    toast.success(
      `Metadata for ${formData.value.pageName} saved successfully!`
    );
    resetForm();
    fetchAllMetadata();
  } catch (error) {
    toast.error("Failed to save metadata: " + error.message);
  }
};

// Edit metadata
const editMetadata = (item) => {
  isEditing.value = true;
  formData.value = {
    pageName: item.pageName,
    metaTagsJson: JSON.stringify(item.metaTags, null, 2),
    description: item.description || "",
  };
};

// Delete metadata
const deleteMetadata = async (pageName) => {
  if (!confirm(`Are you sure you want to delete metadata for ${pageName}?`)) {
    return;
  }

  try {
    await axiosInst.delete(`/api/metadata/${pageName}`);
    toast.success(`Metadata for ${pageName} deleted successfully!`);
    fetchAllMetadata();
  } catch (error) {
    toast.error("Failed to delete metadata: " + error.message);
  }
};

// Reset form
const resetForm = () => {
  isEditing.value = false;
  formData.value = {
    pageName: "",
    metaTagsJson: "",
    description: "",
  };
};

onMounted(() => {
  fetchAllMetadata();
});
</script>

<style scoped>
/* Page-specific tweaks only — shared look comes from admin-theme.css */

/* Collapsible init section */
.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: none;
  border: none;
  padding: 0.5rem;
  margin: -0.5rem;
  cursor: pointer;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--g-text);
  border-radius: var(--g-radius-sm);
  transition: background-color 0.2s ease;
}

.section-header:hover {
  background: var(--g-hover);
}

.chevron {
  transition: transform 0.3s ease;
  font-size: 13.5px;
  color: var(--g-blue);
}

.chevron.expanded {
  transform: rotate(90deg);
}

.section-content {
  padding-top: 14px;
}

.info {
  margin-top: 0.75rem;
}

/* Form */
.form-group {
  margin-bottom: 14px;
}

.mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.button-group {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.list-title {
  margin: 0 0 12px;
}

/* Metadata Cards */
.metadata-card {
  margin-bottom: 12px;
}

.card-header {
  background: var(--g-surface-2);
  padding: 11px 14px;
  border-bottom: 1px solid var(--g-border);
}

.card-title {
  margin: 0 0 2px 0;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--g-blue);
}

.card-content {
  padding: 14px;
  background: var(--g-surface);
}

.card-content pre {
  background: var(--g-surface-2);
  padding: 12px;
  border-radius: var(--g-radius-sm);
  overflow: auto;
  font-size: 13.5px;
  max-height: 300px;
  margin: 0;
  color: var(--g-text);
}

.card-actions {
  padding: 10px 14px;
  background: var(--g-surface-2);
  border-top: 1px solid var(--g-divider);
  display: flex;
  gap: 8px;
}
</style>
